import { OnDestroy, OnInit, Directive } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ConfigService } from '@finder/core/config.service';
import { SITE_ID } from '@finder/shared/constants/app.constants';
import { MultiSelectService } from '@finder/shared/services/multi-select/multi-select.service';
import { IMultiSelectService } from '@finder/shared/services/multi-select/multi-select-service.interface';

@Directive()
export abstract class MultiSelectDirective implements OnInit, OnDestroy {

    multiSelect: IMultiSelectService;
    _labels: any;

    protected destroy$ = new Subject<void>();
    protected keys = ['Space', 'Enter'];

    constructor(
        protected multiSelectService: MultiSelectService,
        protected configService: ConfigService,
    ) {}

    ngOnInit() {
        this.multiSelectService.watchOptions()
            .pipe(takeUntil(this.destroy$))
            .subscribe((multiSelect: IMultiSelectService) => {
                this.multiSelect = multiSelect;
                this.setOptionSelected();
            });

        this.multiSelectService.watchSelectedOptions()
            .pipe(takeUntil(this.destroy$))
            .subscribe((options: string[]) => this.updateSelectedOptions(options));
    }

    ngOnDestroy() {
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * On keydown handler
     * Select All Locations
     *
     * @param event
     */
    onSelectAll(event) {
        if (this.keys.indexOf(event.code) === -1) {
            return;
        }

        return this.checkUnselectAllOption(event);
    }

    /**
     * On click handler
     * Select All Locations
     *
     * @param event
     */
    onClickAll(event) {
        return this.checkUnselectAllOption(event);
    }

    /**
     * Check if there is some option selected.
     */
    isSomeSelected() {
        const result = this.multiSelect.options.some(opt => opt.checked);

        return result;
    }

    /**
     * Check if All options are selected.
     */
    isAllSelected() {
        for (const option of this.multiSelect.options) {
            if (option.checked !== true) {
                return false;
            }
        }

        return true;
    }

    /**
     * Apply new value to all the avaiable options.
     *
     * @param value
     */
    applyAll(value) {
        this.multiSelect.options = this.multiSelect.options.map(option => {
            option.checked = value;

            return { ...option };
        });
    }

    /**
     * Check new option and update the selected value.
     *
     * @param event
     * @param option
     */
    onCheck(event, option) {
        if (option.checked === event.detail.value) {
            return;
        }
        option.checked = event.detail.value;
        this.updateSelected(option);
        this.validateSelectAll();
    }

    /**
     * Check if the All Locations option can be unchecked.
     *
     * @param event
     */
    checkUnselectAllOption(event) {
        const newValue = !this.multiSelect.allOption.checked;
        if (!newValue && !this.isSomeSelected()) {
            // if there is nothing selected prevent
            // the user to be able to unselect the 'select all' checkbox
            event.preventDefault();
            // The stopImmediatePropagation() method prevents other listeners of
            // the same event from being called.
            // So we can stop the checkbox polymer component from changing the value of the box.
            event.stopImmediatePropagation();

            return false;
        }
    }

    /**
     * Check All Locations option and notify subscribers.
     *
     * @param event
     */
    onCheckAll(event) {
        const newValue = event.detail.value;
        if (this.multiSelect.allOption.checked === newValue) {
            return;
        }
        this.multiSelect.allOption.checked = newValue;
        if (newValue) {
            this.applyAll(false);
            this.multiSelect.selected = [];
        }
        this.multiSelectService.updateOptions(this.multiSelect);
    }

    updateSelectedOptions(options: string[]) {
        this.multiSelect.options.forEach(option => {
            option.checked = options.includes(option.key);
            this.updateSelected(option);
        });
        this.validateSelectAll();
    }

    /**
     * Update single option.
     *
     * @param option
     */
    updateSelected(option) {
        if (option.checked) {
            this.multiSelect.selected.push(option.key);
        } else {
            this.multiSelect.selected = this.multiSelect.selected.filter(key => key !== option.key);
        }
        this.setOptionSelected();
    }

    setOptionSelected() {
        if (this.configService.getSiteId() === SITE_ID.hkdl && this.multiSelect.selected.length === 0) {
            this.multiSelect.optionSelected = `${this._labels.allLocation}`;

            return;
        }

        if (this.multiSelect.selected.length !== 1) {
            this.multiSelect.optionSelected = `${this.multiSelect.selected.length || this._labels.allText} ${this._labels.locationText}`;

            return;
        }

        const optionSelected = this.multiSelect.options.find(option => option.key === this.multiSelect.selected[0]);

        if (!optionSelected) {
            this.multiSelect.optionSelected = '';

            return;
        }
        this.multiSelect.optionSelected = optionSelected.value;
    }

    /**
     * Validate All Locations option.
     */
    validateSelectAll() {
        if (this.isAllSelected() || !this.isSomeSelected()) {
            this.onCheckAll({ detail: { value: true } });
        } else {
            this.multiSelect.allOption.checked = false;
            this.multiSelectService.updateOptions(this.multiSelect);
        }
    }
}
